/**
 * The records a run planned to move that have no recorded outcome — `unaccounted()`.
 *
 * A sibling BELOW the phase stack, not a slot inside it: each `PhaseStage` row reports what one phase
 * measured, and this number is precisely what no phase measured. It is the only genuinely new element
 * on this surface, and it exists because 1,230 records abandoned by a stopped sync (`failedStoppedMidSend`)
 * currently appear nowhere at all — the card says "0 / 1.2K" and stops.
 *
 * THE WORDING RULE, which is the whole point of this component: same arithmetic, opposite word. Mid-run
 * the gap is work in flight and entirely healthy; on a run that died it is abandoned. Calling live work
 * "abandoned" would be a plain lie, so the word is picked from `isRunning(run)` and never fixed. Both
 * directions of that predicate matter, not just the obvious one:
 *  - an aborted run is not running, so it reads "abandoned" — those records genuinely went nowhere;
 *  - a run the platform reports as `succeeded` but STILL carries a real, positive shortfall ALSO reads
 *    "abandoned", not "in flight" — it is settled, so there is no more flight left for the gap to be
 *    in. `status === 'failed'` would get this case wrong: it would call a self-reported success
 *    "in flight" forever, which is not what it is.
 *
 *    One candidate explanation for such a gap is the hole `unaccounted()`'s doc names — a destination
 *    abort whose HTTP status is neither 400 nor 401 written to no metric at all. CARRY THE QUALIFIER
 *    THAT DOC CARRIES: it is labelled "UNVERIFIED, no fixture … read from rudder-server source, not
 *    observed on the wire", and this file previously restated it as flat fact. The wording rule above
 *    does not depend on it either way — a settled run with a positive gap reads "abandoned" whatever
 *    opened the gap — so nothing here needs the mechanism to be true.
 *
 * Silent in three cases, each for its own reason:
 *  - the balance holds (`gap <= 0` — nothing to say);
 *  - every planned record was refused (`deliveryOutcome === 'refused'` is a gap WITH an owner — the
 *    Send row already reports it — and is already excluded by `gap <= 0`, since a refusal is counted
 *    in `failed` and so is landed, not unaccounted; `RunShortfall.test.tsx` keeps a fixture pinning
 *    this so a future change to `unaccounted()` cannot silently start double-reporting refusals);
 *  - `unaccounted()` returns `null` — either the plan size is the `-1` sentinel, or the run hasn't
 *    reached its first real count yet (`!isCounted(run)`). The brief for this component also listed a
 *    third `null` reason, "`!isCounted(run)`", as a separate guard clause alongside `gap === null`; it
 *    is dropped here because `unaccounted()`'s own first line already returns `null` in that case (read
 *    directly, `lib/run-view.ts`), so the clause could never fire — keeping it would imply a case this
 *    function can reach that it cannot.
 */
import { cn } from '@/_shared/lib/utils';

import { exact, isRunning, unaccounted } from '../lib/run-view';
import type { RawSyncRun } from '../types';

interface RunShortfallProps {
  run: RawSyncRun;
  className?: string;
}

export function RunShortfall({ run, className }: RunShortfallProps): React.ReactElement | null {
  const gap = unaccounted(run);
  if (gap === null || gap <= 0) return null;

  const live = isRunning(run);
  return (
    <p className={cn('pt-3 text-xs', live ? 'text-muted-foreground' : 'text-danger', className)}>
      {exact(gap)} records {live ? 'in flight' : 'abandoned'}
    </p>
  );
}
